<template>
  <main>
    <div class="header md-elevation-1">
      <gl-logo-app onDarkBackground class="logo" />
      <div class="align-left">
        <md-tabs
          :md-active-tab="activeTab"
          @md-changed="$emit('update:activeTab', $event)"
        >
          <md-tab
            id="filter"
            md-label="分析条件"
            md-icon="filter_list"
          ></md-tab>
          <md-tab
            id="result"
            md-label="分析結果"
            md-icon="table_chart"
            :md-disabled="!queryId"
          ></md-tab>
        </md-tabs>
      </div>
      <div class="header-actions">
        <transition name="slide-in">
          <div
            v-if="activeTab === 'result' && !querying"
            class="header-actions"
          >
            <slot name="header-actions" :query-id="queryId" />
            <div
              :gl-tooltip="
                activeTab === 'result' && !querying ? 'Excel出力' : ''
              "
              gl-tooltip-direction="down"
            >
              <gl-button
                :disabled="
                  activeTab !== 'result' || querying || downloadingExcel
                "
                @click="downloadExcel"
                icon-button
              >
                <md-icon :class="{ spin: downloadingExcel }">
                  <font-awesome-icon icon="file-excel" />
                </md-icon>
              </gl-button>
            </div>
          </div>
        </transition>
        <div class="header-actions">
          <div
            v-if="$route.meta.page.hasHelp"
            gl-tooltip="ヘルプ"
            gl-tooltip-direction="down"
          >
            <gl-button icon-button @click="openHelp">
              <gl-icon-help />
            </gl-button>
          </div>
          <div :gl-tooltip="menu.menuLongName" gl-tooltip-direction="down">
            <md-avatar class="avatar">
              <img :src="menu.imageUrl" />
            </md-avatar>
          </div>
        </div>
      </div>
    </div>

    <!-- 条件項目一覧 -->
    <div class="filter-summary-wrapper">
      <div class="filter-summary">
        <md-list class="filter-summary-body md-double-line">
          <slot name="filter-summary">
            <component
              v-for="p of filterSummaryProperties"
              :key="p.name"
              :is="`filter-summary-${p.name}`"
              :scroll-to-id="p.name"
              v-bind="p.filter"
            />
          </slot>
        </md-list>
      </div>
    </div>

    <!-- コンテンツ -->
    <div class="contents">
      <div v-if="activeTab === 'filter'" id="filter" class="filter">
        <slot name="filter"></slot>
      </div>
      <div
        v-if="activeTab === 'result'"
        class="result-container"
        ref="resultContainer"
      >
        <div v-if="querying" class="spinner-container">
          <img src="@/assets/images/Web_hosting.svg" />
          <spinner />
        </div>
        <slot v-else name="result"></slot>
      </div>

      <transition name="query-button">
        <div
          v-if="activeTab === 'filter'"
          class="fixed-action-area"
          :gl-tooltip="buttonToolTip"
          gl-tooltip-direction="left"
        >
          <button-execute-query
            @click="executeQueryFilterSaveWrapper"
            class="animated"
            :disabled="!canStartQuery"
            :will-plane-fly="canStartQuery"
            :class="{ tada: canStartQuery }"
          />
        </div>
      </transition>
    </div>

    <!-- 顧客コレクション -->
    <customer-collection
      :customers="customers"
      @update:customers="updateCustomers"
      @download="downloadCustomers"
      :downloading="downloadingCustomerFile"
    />

    <!-- 顧客ファイルアップロード中通知 -->
    <md-dialog
      :md-active.sync="uploadingCustomerFile"
      :md-close-on-esc="false"
      :md-click-outside-to-close="false"
    >
      <md-dialog-content>
        <div style="display:flex;align-items:center">
          <spinner :diameter="30" />
          <span style="margin-left:var(--spacing-medium)">
            顧客一覧ファイルをアップロードしています...
          </span>
        </div>
      </md-dialog-content>
    </md-dialog>

    <md-dialog-alert
      :md-active.sync="hasCustomerFileUploadWarning"
      :md-content="customerFileUploadWarningMessage"
      md-confirm-text="Close"
    />

    <!-- 分析結果0件警告 -->
    <md-dialog-alert
      :md-active.sync="showNoResultDialog"
      md-content="対象となるデータがありませんでした"
      md-confirm-text="Close"
    />

    <!-- 分析条件復元失敗エラーダイアログ -->
    <md-dialog-alert
      :md-active.sync="showFailedToApplyQueryFilter"
      md-content="分析条件の読込に失敗しました。保存されていた分析条件は削除されます。"
      md-confirm-text="Close"
    />
  </main>
</template>

<script>
import { kebabCase, mergeWith, isArray, differenceBy } from 'lodash'
import CustomerCollection from '@/components/CustomerCollection.vue'
import ButtonExecuteQuery from '@/components/ButtonExecuteQuery'
import Spinner from '@/components/Spinner.vue'
import DialogWarning from '@/components/DialogWarning.vue'

/**
 * 分析条件サマリコンポーネントを非同期コンポーネントのローカル登録形式にして取得する
 *
 * ```
 * components
 * └ FilterSummaryItem.vue
 * └ FilterSummaryOption.vue
 * └ FilterSummaryStore.vue
 * ```
 * ↓
 * ```
 * {
 *   FilterSummaryItem: () => import('../FilterSummaryItem.vue'),
 *   FilterSummaryOption: () => import('../FilterSummaryOption.vue'),
 *   FilterSummaryStore: () => import('../FilterSummaryStore.vue'),
 * }
 * ```
 */
function loadSummaryComponents() {
  const requireComponent = require.context(
    '../',
    false,
    /FilterSummary[\w-]+\.vue$/,
  )
  return requireComponent
    .keys()
    .map(fileName => {
      const name = fileName.replace(/^\.\//, '').replace(/\.\w+$/, '')

      return {
        name,
        import: () => import(`../${name}.vue`),
      }
    })
    .reduce((o, c) => ({ ...o, [c.name]: c.import }), {})
}

// function getMenuNameFrom(menuCode, store) {
//   const menu = store.state.menus.find(m => m.menuCode === menuCode)
//   if (!menu) {
//     return undefined
//   }
//   return menu.menuLongName
// }

/**
 * 分析画面基本レイアウト
 *
 * 分析条件・分析結果から成る画面の基本レイアウトです。分析条件を配置するスロットと
 * 分析結果を配置するスロットを提供します。
 *
 * イベント一覧
 *
 * * update:activeTab (tabId: String)
 *   * 開いているタブを変更したとき
 * * query-id-change (queryId: String)
 *   * 画面表示している分析IDが変化したとき
 * * update:resultFetchUrl (url: String)
 *   * 結果FetchURLが変化したとき
 * * update:columns (columns: Array<ColModel>)
 *   * 列定義が変化したとき
 * * update:groupHeader (groupHeaders: Array<Array<GroupHeader>>)
 *   * 列ヘッダ定義が変化したとき
 * * result-area-size-change ({ width: Number, height: Number })
 *   * 結果表示エリアのサイズが変化したとき
 * * query-completed (QueryInfo)
 *   * クエリが正常終了したとき TODO 異常終了した場合のイベントが必要になったら別途作る
 * * update:customers (customers: Array<Customer>)
 *   * 顧客コレクションの内容が変化したとき
 */
export default {
  components: {
    CustomerCollection,
    Spinner,
    ButtonExecuteQuery,
    ...loadSummaryComponents(),
  },
  props: {
    /**
     * クエリリソース名
     *
     * この名前を基準にクエリAPIのエンドポイントの構築等を行います。
     */
    resourceName: {
      type: String,
      required: true,
    },
    /**
     * 分析条件
     *
     * クエリを発行する際のペイロードの元データです。
     * 分析条件サマリを作成、および分析リクエストを投げるために必要です。
     */
    queryFilter: {
      type: Object,
      required: true,
    },
    /**
     * 分析条件をクエリのペイロードに変換する関数
     *
     * これを使ってクエリを発行する際のペイロードに変換します。
     * 分析条件のオブジェクトは画面に依ってバラバラなので、変換処理も画面が定義して
     * 渡してくれる必要があります。
     */
    mapQueryFilterForApi: {
      type: Function,
      required: true,
    },
    /**
     * クエリフィルタオブジェクトを自身のクエリフィルタへ適用する関数
     */
    applyQueryFilter: {
      type: Function,
      required: true,
    },
    /**
     * 分析結果をFetchするためのURL
     *
     * このコンポーネントではなく、分析結果テーブルを持つ側が必要なデータですが、
     * $longQueryから取得するものなのでこちらに寄せました。
     *
     * 実態は、親コンポーネントとの同期のためだけに存在します。
     */
    resultFetchUrl: {
      type: String,
    },
    /**
     * 分析結果の列
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * 分析結果のグループヘッダ
     */
    groupHeader: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択されているタブ（分析条件:filter or 分析結果:result）
     */
    activeTab: {
      type: String,
      default: 'filter',
      validator(v) {
        return ['filter', 'result'].includes(v)
      },
    },
    /**
     * 選択顧客
     */
    customers: {
      type: Array,
      default: () => [],
    },
    /**
     * 顧客選択時の操作が顧客ダウンロードのみ
     *
     * trueの場合、顧客ダウンロードのみであり、画面遷移はできない。
     */
    customerDownloadOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Excel出力処理で指定するクエリパラメータを返す関数
     *
     * 例）
     * {
     *   sortColName: "store_code",
     *   sortOrder: "asc"
     * }
     *
     * => sortColumnName=code&sortOrder=asc
     */
    excelPropertiesHandler: {
      type: Function,
      default: () => ({}),
    },
  },
  data() {
    return {
      /**
       * 分析ID
       *
       * 分析を行う度にこの分析IDが更新されます。
       */
      queryId: undefined,
      /**
       * クエリ実行中か否か
       */
      querying: false,
      /**
       * 顧客一覧ファイルのアップロード中フラグ
       */
      uploadingCustomerFile: false,
      /**
       * 顧客一覧ファイルアップロード時の警告有無
       */
      hasCustomerFileUploadWarning: false,
      /**
       * 顧客一覧ファイルアップロード時の警告メッセージ
       *
       * ファイルフォーマット不正や結果0件
       */
      customerFileUploadWarningMessage: undefined,
      /**
       * 顧客一覧ファイルダウンロード中フラグ
       *
       * ダウンロードURLが作成されるまでには少々時間がかかるので、ボタン制御等
       * をするためにフラグを用意します。
       */
      downloadingCustomerFile: false,
      /**
       * Excelファイルダウンロード中フラグ
       */
      downloadingExcel: false,
      /**
       * 画面遷移のための一時顧客グループ作成中フラグ
       */
      creatingCustomerGroup: false,
      /**
       * 分析結果0件ダイアログの開閉状態
       */
      showNoResultDialog: false,
      /**
       * 分析条件の復元失敗エラーダイアログの開閉状態
       */
      showFailedToApplyQueryFilter: false,

      /**
       * 遷移時に即時分析するか
       */
      immediate: true,
    }
  },
  computed: {
    /**
     * 分析条件サマリ用プロパティリスト
     *
     * 分析画面の分析条件から構築されます。
     *
     * 各分析条件（"期間"や"店舗"の粒度）のフォーマットは以下の通り。
     *
     * ```
     * {
     *   order: <Number | required> 指定がない場合サマリは作成されません
     *   summaryKey: <String | Optional>
     * }
     * ```
     *
     * 分析条件のトップレベルキーからサマリコンポーネントを特定します（
     * `filter-summary-${キーのケバブケース}`）。例えば以下の場合、
     *
     * ```
     * {
     *   term: {
     *     // 略
     *   },
     *   // 略
     * }
     * ```
     *
     * FilterSummaryTerm（filter-summary-term）コンポーネントが
     * 分析条件サマリとして使用されます。
     *
     * 使用するサマリコンポーネント名は、`summaryKey`を含めることで
     * 上書きすることができます。例えば以下の場合、
     *
     * ```
     * {
     *   term: {
     *     // 略
     *     summaryKey: 'termCompare',
     *   },
     *   // 略
     * }
     * ```
     *
     * FilterSummaryTermCompare（filter-summary-term-compare）
     * コンポーネントが分析条件サマリとして使用されます。
     *
     * また、orderが指定されていない条件はサマリを作成しません。
     */
    filterSummaryProperties() {
      return Object.entries(this.queryFilter)
        .filter(entry => entry[1].order !== undefined)
        .map(([name, filter]) => ({
          name: filter.summaryKey
            ? kebabCase(filter.summaryKey)
            : kebabCase(name),
          filter,
        }))
        .sort((p1, p2) => p1.filter.order - p2.filter.order)
    },
    /**
     * クエリの発行ができる状態か否か
     */
    canStartQuery() {
      return !this.hasInvalidInput
    },
    /**
     * 分析ボタンツールチップ
     */
    buttonToolTip() {
      if (this.hasInvalidInput) {
        return '分析条件に入力エラーがあります。'
      }
      return ''
    },
    /**
     * 分析条件に不正があるか
     *
     * 各分析条件から`errors`を読み取りエラーテキストが入っているか確認します。
     *
     * ```
     * queryFilter {
     *   term: {
     *     // 略
     *     errors: [] // ★ここを見る
     *   },
     *   store: {
     *     // 略
     *     errors: [ "店舗を選択してください" ] // ★ここを見る
     *   }
     * }
     * ```
     */
    hasInvalidInput() {
      return (
        Object.entries(this.queryFilter)
          .map(e => e[1])
          .map(e => (e['errors'] ? e.errors : []))
          .filter(e => e.length > 0).length > 0
      )
    },
    menu() {
      const menu = this.$store.state.menus.find(
        m => m.menuCode === this.resourceName,
      )
      if (!menu) {
        return {
          menuCode: undefined,
          menuLongName: undefined,
          imageUrl: undefined,
        }
      }
      return menu
    },
  },
  watch: {
    queryId(queryId) {
      this.$emit('query-id-change', queryId)
    },
    activeTab() {
      // 分析条件、分析結果切替時には顧客コレクションクリア
      this.$emit('update:customers', [])
    },
    //
    // queryIdの変化で実施することは以下の3つ
    //
    // 1. 分析IDが提供されている
    //
    //    既にサーバーサイドの分析が完了しているので、この分析IDで結果を取得して表示します。
    //
    // 2. 参照分析IDが提供されている
    //
    //    画面遷移により開かれた画面です。参照分析IDで指定された分析IDの分析条件
    //    を使い（一部変更）このResourceで分析します。分析が完了したら分析IDをURLに
    //    セットし、1に飛ばします（結果の表示は1のルートに任せます）。
    //
    // 3. 分析IDも参照分析IDもない
    //
    //    メニューから開いた初期画面です。分析後の画面から戻ってきた場合もこのケースです。
    //    分析条件等を初期状態に戻すのを忘れずに。
    //
    '$route.params.queryId': {
      // 画面表示時にもキャッチできるようにimmediateとしています
      immediate: true,
      async handler(queryId) {
        const referenceId = this.$route.query.reference

        if (queryId) {
          // 1. 分析IDが提供されている
          this.$emit('update:activeTab', 'result')

          this.querying = true
          this.$queryNotification.connect(queryId)

          this.queryId = queryId
        } else if (referenceId) {
          // ※テンポラリTapirでは不使用

          // 2. 参照分析IDが提供されている
          const queryFilter = await this.$longQuery.fetchQueryFilter(
            referenceId,
          )
          const override = JSON.parse(this.$route.query.override)

          const mergedQueryFilter = mergeWith(queryFilter, override, (o, s) => {
            return isArray(s) ? s : undefined
          })

          const promises = this.applyQueryFilter(mergedQueryFilter)
          if (promises && promises.length > 0) {
            await Promise.all(promises.filter(p => p))
          }

          if (override.immediate) {
            this.executeQuery()
          }
        } else {
          // 3. 分析IDも参照分析IDもない
          this.queryId = undefined
          this.$emit('update:activeTab', 'filter')

          await this.$nextTick()

          // 保存されている最後の分析条件（画面毎）があれば復元する
          const queryFilter = await this.$longQuery.fetchMenuQueryFilter(
            this.resourceName,
          )
          if (queryFilter) {
            try {
              const promises = this.applyQueryFilter(queryFilter)
              if (promises && promises.length > 0) {
                await Promise.all(promises.filter(p => p))
              }
            } catch (error) {
              // バージョンアップなどで内部データ構造が変わると分析条件の復元が
              // できなくなる可能性がある。ひとまず対策として当該データを削除する。
              this.showFailedToApplyQueryFilter = true
              await this.$longQuery.deleteMenuQueryFilter(this.resourceName)
            }
          }
        }
      },
    },
  },
  methods: {
    async executeQueryFilterSaveWrapper() {
      await this.$longQuery.saveMenuQueryFilter(
        this.resourceName,
        this.queryFilter,
      )
      await this.executeQuery()
    },
    async executeQuery() {
      this.$emit('update:customers', [])

      // 顧客ファイルの指定がある場合、そのままクエリAPIには送信できないので、
      // 一旦アップロードして一時顧客グループIDを取得してから分析に進む。
      if (
        this.queryFilter.customer &&
        !this.queryFilter.customer.customerGroupId &&
        this.queryFilter.customer.file
      ) {
        const customerGroup = await this.uploadCustomerFile(
          this.queryFilter.customer.file,
        )

        if (customerGroup.invalidFile) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルが読み込めませんでした。'
          return
        }

        if (customerGroup.numRows === 0) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルに有効な顧客が存在しませんでした。'
          return
        }

        this.queryFilter.customer.customerGroupId =
          customerGroup.customerGroupId
      }

      this.querying = true

      let payload = this.mapQueryFilterForApi(this.queryFilter)

      try {
        // Promiseの完了を待機する。オブジェクトが返ってきた場合、payloadには
        // 値がそのまま格納され後続の処理が行われる。
        payload = await Promise.resolve(payload)
      } catch (error) {
        if (error.name === 'AppError') {
          // 論理エラーは分析処理を行わないだけ
          this.$dialog.open(DialogWarning, error.message)
          return
        } else {
          throw error
        }
      }

      const queryInfo = await this.$longQuery.executeQuery(
        this.resourceName,
        payload,
      )

      // クエリIDがパスパラメータに含まれた時点で分析進捗確認を
      // 開始するのでここではまだconnectしません。
      // this.$queryNotification.connect(queryInfo.id)
      // 分析条件はサーバーサイドから取得するので保存はしません。
      // await this.$longQuery.saveQueryFilter(queryInfo.id, this.queryFilter)

      const nextPage = `/query-${this.resourceName}/${queryInfo.id}`
      if (this.$route.query.reference) {
        this.$router.replace(nextPage)
      } else {
        this.$router.push(nextPage)
      }

      // this.$emit('update:activeTab', 'result')
    },
    /**
     * 顧客一覧ファイルをアップロードして一時顧客グループを作成する
     */
    async uploadCustomerFile(file) {
      this.uploadingCustomerFile = true
      const customerGroup = await this.$customerGroup.upload(file)
      this.uploadingCustomerFile = false

      return customerGroup
    },
    /**
     * クエリの進捗通知を受信したときの処理
     */
    onMessage(queryInfo) {
      if (this.queryId !== queryInfo.id) {
        return
      }

      this.querying = false

      if (queryInfo.status === 'Error') {
        throw new Error('分析時にエラーが発生しました。')
      }

      this.loadQueryResult(queryInfo.id)

      // 結果表示用URLに遷移するが、実態はこの画面
      // const nextPage = `/query-${this.resourceName}/${queryInfo.id}`
      // if (this.$route.query.reference) {
      //   this.$router.replace(nextPage)
      // } else {
      //   this.$router.push(nextPage)
      // }
    },
    async loadQueryResult(queryId) {
      // TODO 分析条件を反映しているが、実際に必要になるのは戻る/進むなどでURLの
      //      クエリIDが変化したときだけなので、この処理を実行する/しないを何かで判定する

      let queryInfo

      try {
        queryInfo = await this.$longQuery.fetchQueryInfo(
          this.resourceName,
          queryId,
        )
      } catch (error) {
        if (error.response.status === 404) {
          alert(`分析ID: ${queryId} は存在しないか、既に削除されています。`)
          return
        }
        throw error
      }

      const queryFilter = JSON.parse(queryInfo.queryFilter)
      if (!queryFilter) {
        throw new Error('分析IDが見つかりませんでした。', queryId)
      }

      const promises = this.applyQueryFilter(queryFilter)
      if (promises && promises.length > 0) {
        await Promise.all(promises.filter(p => p))
      }

      const url = await this.$longQuery.getQueryResultFetchUrl(
        this.resourceName,
        queryId,
      )

      if (!queryInfo.hasData) {
        this.showNoResultDialog = true
        this.$emit('update:activeTab', 'filter')
        return
      }

      // this.$emit('update:activeTab', 'result')

      await this.$nextTick()

      const elem = this.$refs.resultContainer
      this.$emit('result-area-size-change', {
        width: elem.clientWidth - 2,
        height: elem.clientHeight,
      })

      this.$emit('update:groupHeaders', queryInfo.grid.groupHeader)
      this.$emit('update:columns', queryInfo.grid.colModel)
      this.$emit('update:resultFetchUrl', url)

      this.$emit('query-completed', queryInfo)
    },
    /**
     * 顧客コレクションへの登録
     */
    addCustomerCollection(customer) {
      const cells = document.querySelectorAll(
        `[data-customer-collection="${customer.id}"]`,
      )

      for (const cell of cells) {
        cell.classList.toggle('customer-collection-selected')
      }

      this.$emit('update:customers', [customer, ...this.customers])
    },
    /**
     * 顧客コレクションからの削除
     */
    removeCustomerCollection(customer) {
      const cells = document.querySelectorAll(
        `[data-customer-collection="${customer.id}"]`,
      )

      for (const cell of cells) {
        cell.classList.toggle('customer-collection-selected')
      }

      this.$emit(
        'update:customers',
        this.customers.filter(c => c.id !== customer.id),
      )
    },
    /**
     * 顧客コレクションに存在すれば削除、存在しなければ追加
     */
    toggleCustomerCollection(customer) {
      if (this.customers.find(c => c.id === customer.id)) {
        this.removeCustomerCollection(customer)
      } else {
        this.addCustomerCollection(customer)
      }
    },
    /**
     * 顧客コレクション内容変更
     */
    updateCustomers(customers) {
      // 顧客コレクションでの選択を反映させる。
      // 現状顧客コレクション上では削除しかできないので、現顧客リストにあって
      // 新顧客リストにない顧客のセルの選択済みCSSクラスを削除する対応のみ
      differenceBy(this.customers, customers, 'id').forEach(customer => {
        const cells = document.querySelectorAll(
          `[data-customer-collection="${customer.id}"]`,
        )
        for (const cell of cells) {
          cell.classList.remove('customer-collection-selected')
        }
      })

      // ※ これは全セル走査して同期させる実装（顧客コレクション上の操作が削除だけでは
      //    なくなったらこんなのも必要になるかも。パフォーマンスは考慮していない、、、）
      // const cells = document.querySelectorAll(`[data-customer-collection]`)
      // const ids = new Set(customers.map(c => c.id))
      // for (const cell of cells) {
      //   if (ids.has(cell.getAttribute('data-customer-collection'))) {
      //     cell.classList.add('customer-collection-selected')
      //   } else {
      //     cell.classList.remove('customer-collection-selected')
      //   }
      // }

      this.$emit('update:customers', customers)
    },
    /**
     * 顧客一覧のダウンロード
     *
     * 顧客のダウンロードは画面に関係なく顧客コレクションに登録されている要素から
     * 実施可能なのでここで行う。
     */
    async downloadCustomers(customers) {
      if (!this.queryId) {
        // 分析結果画面に配置している顧客コレクションからのイベントなので、
        // 分析IDは必ずあるはずなので通常ここは通らない。
        throw new Error('分析IDがありません')
      }
      if (!customers || customers.length === 0) {
        // 顧客が選択されていない状態では顧客コレクションは開いていないので、
        // 通常ここは通らない。
        throw new Error('顧客が選択されていません')
      }

      this.downloadingCustomerFile = true

      try {
        const customerGroup = await this.$customerGroup.create(
          this.queryId,
          customers.map(c => c.id),
        )
        const url = await this.$customerGroup.archive(
          customerGroup.customerGroupId,
        )

        window.open(url)
      } catch (e) {
        this.downloadingCustomerFile = false
        throw e
      }

      this.downloadingCustomerFile = false
    },
    /**
     * Excelファイルをダウンロードする
     *
     * propsであるexcelPropertiesHandlerを呼び出し、Excel作成URLのクエリパラメータとします。
     */
    async downloadExcel() {
      this.downloadingExcel = true

      try {
        const extraProps = this.excelPropertiesHandler()

        const url = await this.$longQuery.getExcelUrl(
          this.resourceName,
          this.queryId,
          extraProps,
        )

        window.open(url)
      } finally {
        this.downloadingExcel = false
      }
    },
    async transition(t) {
      // 画面遷移時には顧客セル選択（->一時顧客グループ作成）が必須
      this.creatingCustomerGroup = true

      let customerGroup
      try {
        customerGroup = await this.$customerGroup.create(
          this.queryId,
          this.customers.map(c => c.id),
        )
        this.creatingCustomerGroup = false
      } catch (error) {
        this.creatingCustomerGroup = false
        throw error
      }

      const nextPage = t.transition({
        destination: t.menuCode,
        queryId: this.queryId,
        override: {
          customer: {
            customerGroupId: customerGroup.customerGroupId,
          },
          immediate: this.immediate,
        },
      })

      window.open(`${process.env.BASE_URL}${nextPage}`)
    },
    openHelp() {
      window.open(this.$route.meta.page.helpUrl)
    },
  },
  async created() {
    // ちょっとキモいが、EventBusを用意するほどでもないので、このレイアウトを使用
    // しているコンポーネントで'toggle-customer-collection'が発生したらこの
    // コンポーネントが顧客リストを操作する。
    this.$parent.$on(
      'toggle-customer-collection',
      this.toggleCustomerCollection,
    )

    // クエリ通知
    const subscription = this.$queryNotification.observable.subscribe(
      this.onMessage,
    )
    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())
  },
  mounted() {
    // refで指定したhtml要素はmountedにならないと見つからないのでCreatedでは以下の
    // 処理はできません。
    this.$windowResize.subscribe(() => {
      const elem = this.$refs.resultContainer
      if (elem) {
        this.$emit('result-area-size-change', {
          width: elem.clientWidth - 2,
          height: elem.clientHeight,
        })
      }
    })
  },
}
</script>

<style lang="scss" scoped>
$header-height: 72px;
$filter-summary-width: 320px;

main {
  display: flex;
  height: 100vh;
  position: relative;
}

.header {
  background: #fff;
  height: $header-height;
  top: 0;
  left: 0;
  right: 0;
  padding-right: var(--spacing-large);
  opacity: 0.9;
  position: fixed;
  z-index: 11;

  display: flex;
  align-items: center;

  .logo {
    flex: 0 0 $filter-summary-width;
    height: $header-height;
    background: var(--bg-color-accent);
  }

  .align-left {
    flex: 1;
    margin-left: var(--spacing-large);
  }
}

.header-actions {
  display: flex;
  align-items: center;
}

.filter-summary-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  overflow: hidden;
  height: 100%;
  width: $filter-summary-width;
  z-index: 10;
}

.filter-summary {
  padding-top: $header-height;
  height: 100%;
  background: var(--bg-color-accent);
  overflow-x: hidden;
  overflow-y: auto;
}

.filter-summary-body {
  flex: 1;
  background: transparent;
}

.contents {
  padding-top: $header-height;
  padding-bottom: var(--spacing-large);
  padding-left: var(--spacing-large);
  padding-right: var(--spacing-large);
  margin-top: var(--spacing-large);
  margin-left: $filter-summary-width;
  width: 100%;
}

.filter {
  // 分析条件内に描画される分析条件パネルの表示位置はここで制御します。
  .query-filter-element {
    margin-top: var(--spacing-large);
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      padding-bottom: var(--spacing-large);
    }
  }
}

.fixed-action-area {
  position: fixed;
  bottom: var(--spacing-medium);
  right: var(--spacing-medium);
  z-index: 11;
}

.result-container {
  height: 100%;
  position: relative;
  width: 100%;
}

.spinner-container {
  position: relative;
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  img {
    position: absolute;
    width: 200px;
    opacity: 0.4;
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
    &:hover {
      opacity: 0.8;
      transform: scale(1.1);
    }
  }
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.slide-in-enter,
.slide-in-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.query-button-enter-active,
.query-button-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.query-button-enter,
.query-button-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.avatar {
  margin-left: var(--spacing-small);
  img {
    object-fit: cover;
  }
}
</style>

<style lang="scss">
.c3t-cell {
  &.customer-collection-cell {
    cursor: pointer;
    color: var(--md-theme-default-primary-on-background, #448aff);
    text-decoration: underline;
  }
  &.customer-collection-selected {
    color: inherit;
    background: #bdbdbd !important;
    text-decoration: none;
  }
}

// C3-Tableのスタイル暫定上書き
.c3t-theme-default {
  .c3t-container {
    background-color: #fff !important;
  }
}

.spin {
  animation: spin 0.4s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
